import { useEffect, useState } from 'react';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { Workplace } from 'types';
import { usePermissionCheck } from '../../../../../core/hooks/usePermissionCheck';
import { Button } from 'components/ui/button';
import { fetchWorkplaces, deleteWorkplace } from 'core/API/workPlace';
import CreateWorkplaceSetting from './CreateWorkplaceSettings';
import ShowWorkplaceSetting from './ShowWorkplaceSettings/ShowWorkplaceSetting';
import styles from '../GeneralSettings.module.scss';
import EditWorkplaceSettings from './EditWorkplaceSettings/EditWorkplaceSetting';
import dayjs from 'dayjs';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { CircularProgress, Box } from '@mui/material';
export interface WorkplaceSettingsProps {
	handleFocusChange?: (focus: 'organization' | 'workplace' | 'user' | null) => void;
	inFocus: 'organization' | 'workplace' | 'user' | 'organizational_hierarchy' | 'security_role' | 'partners' | null;
}	

function WorkplaceSettings(props: WorkplaceSettingsProps) {
	// const queryClient = useQueryClient();
	const { handleFocusChange, inFocus } = props;
	const { hasEditPermission, hasViewPermission, hasDeletePermission } = usePermissionCheck();
	const { showToast } = useToastContext();
	const { workplaceId: currentUserWorkplaceId } = useWorkplacesContext();
	const canViewWorkplace = hasViewPermission('Workplace');
	const canEditWorkplace = hasEditPermission('Workplace');
	const canDeleteWorkplace = hasDeletePermission('Workplace');
	const [workplaces, setWorkplaces] = useState<Workplace[] | null>(null);
	const [workplacesLoading, setWorkplacesLoading] = useState<boolean>(true);
	const [workplacesError, setWorkplacesError] = useState<string | null>(null);
	const [deletingWorkplaceId, setDeletingWorkplaceId] = useState<number | null>(null);

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);

	useEffect(() => {
		// Reset view mode when focus changes back to null
		if (inFocus === null) {
			setViewMode('list');
			setSelectedWorkplace(null);
		}

		const loadWorkplaces = async () => {
			// console.log("loading workplaces...");
			try {
				setWorkplacesLoading(true);
				setWorkplacesError(null);

				const params = {
					limit: 30,
					page: 1,
					create_predictions: 0 as const,
				};

				const response = await fetchWorkplaces(params);
				// Sort workplaces by workplaceUnitNumber as integer
				const sortedWorkplaces = (response || []).sort((a, b) => {
					const unitA = parseInt(a.workplaceUnitNumber || '0');
					const unitB = parseInt(b.workplaceUnitNumber || '0');
					return unitA - unitB;
				});
				setWorkplaces(sortedWorkplaces);
			} catch (err: any) {
				setWorkplacesError(err?.message || 'Failed to fetch workplaces');
			} finally {
				setWorkplacesLoading(false);
			}
		};

		loadWorkplaces();
	}, [inFocus]);

	const handleCreateClick = () => {
		setViewMode('create');
		handleFocusChange('workplace');
	};

	// compute next available unit number from workplaces list
	const nextUnitNumber = (() => {
		if (!workplaces || workplaces.length === 0) return '0001';
		const maxUnit = workplaces.reduce((max: number, wp: any) => {
			const n = parseInt(wp?.workplaceUnitNumber || '0', 10) || 0;
			return Math.max(max, n);
		}, 0);
		return String(maxUnit + 1).padStart(4, '0');
	})();

	const handleBackClick = () => {
		setViewMode('list');
		handleFocusChange(null)
		setSelectedWorkplace(null);
	};

  const handleShowClick = (workplace: Workplace) => {
		if (!canViewWorkplace) return;
		setSelectedWorkplace(workplace);
		handleFocusChange('workplace');
		setViewMode('show');
  };
  
  const handleEditClick = (workplace: Workplace) => {
		if (!canEditWorkplace) return;
		handleFocusChange('workplace');
    setSelectedWorkplace(workplace);
		setViewMode('edit');
  }

	const handleDeleteClick = async (workplace: Workplace) => {
		if (!canDeleteWorkplace) return;
		if (!workplace.id) {
			console.error('Workplace ID is missing');
			return;
		}

		// Check 1: If there's only one workplace and it's Default, don't allow deletion
		if (workplaces && workplaces.length === 1 && workplace.name.toLowerCase() === 'default') {
			showToast('error', 'Cannot delete the only remaining default workplace.');
			return;
		}

		// Check 2: If user is trying to delete their current workplace
		if (currentUserWorkplaceId === workplace.id) {
			showToast('error', 'Cannot delete a workplace you are logged into.');
			return;
		}

		// Check 3: Allow deletion of default workplace if user is logged into a different workplace
		// This is already handled by the above checks - if user is in different workplace, they can delete default

		const isConfirmed = window.confirm(`Are you sure you want to delete workplace "${workplace.name}"? This action cannot be undone.`);
		
		if (!isConfirmed) {
			return;
		}

		try {
			const workplaceId = Number(workplace.id);
			setDeletingWorkplaceId(workplaceId);
			await deleteWorkplace(workplaceId);
			
			// Remove the deleted workplace from the local state
			setWorkplaces(prevWorkplaces => 
				prevWorkplaces ? prevWorkplaces.filter(wp => wp.id !== workplace.id) : null
			);
			
			showToast('success', `Workplace "${workplace.name}" deleted successfully.`);
		} catch (error: any) {
			console.error('Error deleting workplace:', error);
			showToast('error', error?.message || 'Failed to delete workplace. Please try again.');
		} finally {
			setDeletingWorkplaceId(null);
		}
	};

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* Workplaces Card */}
					<div className={styles.workplaceCard}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Workplaces</h2>
							<Button 
								className={styles.addButton} 
								onClick={handleCreateClick}
								disabled={!canEditWorkplace}
								title={canEditWorkplace ? "Add new workplace" : "You do not have permission to add workplaces"}
								style={{ opacity: canEditWorkplace ? 1 : 0.5, cursor: canEditWorkplace ? 'pointer' : 'not-allowed' }}
							>
								Add
							</Button>
						</div>

					{workplacesError && <div className={styles.errorMessage}>{workplacesError}</div>}

					{workplacesLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
					<CircularProgress size={20} color="inherit" />
						</Box>
					) : workplaces && workplaces.length !== 0 ? (
							<div className={styles.tableContainer} style={{ maxHeight: '500px', overflow: 'auto' }}>
								<table className={styles.workplaceTable}>
									<thead>
										<tr>
											<th>Unit Number</th>
											<th>Name</th>
											<th>City</th>
											<th>State</th>
											<th>Open Date</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>View</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
										</tr>
									</thead>
									<tbody>
										{workplaces.map((workplace: any) => (
											<tr key={workplace?.id}>
												<td>{workplace?.workplaceUnitNumber}</td>
												<td>{workplace.name}</td>
												<td>{workplace?.city}</td>
												<td>{workplace?.state}</td>
												<td>{workplace?.openDate ? dayjs(workplace.openDate).format('MM/DD/YYYY') : dayjs(workplace.openDate).format('MM/DD/YYYY')}</td>
												<td>
													<button
														className={styles.iconButton}
														onClick={() => handleShowClick(workplace)}
														title={canViewWorkplace ? "View Workplace" : "You do not have permission for this action"}
														disabled={!canViewWorkplace}
														style={{ opacity: canViewWorkplace ? 1 : 0.5, cursor: canViewWorkplace ? 'pointer' : 'not-allowed' }}
													>
														<Visibility className={styles.editIcon} />
													</button>
												</td>
												<td>
													<button
														className={styles.iconButton}
														onClick={() => handleEditClick(workplace)}
														title={canEditWorkplace ? "Edit Workplace" : "You do not have permission for this action"}
														disabled={!canEditWorkplace}
														style={{ opacity: canEditWorkplace ? 1 : 0.5, cursor: canEditWorkplace ? 'pointer' : 'not-allowed' }}
													>
														<Edit className={styles.editIcon} />
													</button>
												</td>
												<td>
													<button
														className={styles.iconButton}
														onClick={() => handleDeleteClick(workplace)}
														disabled={!canDeleteWorkplace || deletingWorkplaceId === Number(workplace.id)}
														title={canDeleteWorkplace ? "Delete Workplace" : "You do not have permission for this action"}
														style={{ opacity: canDeleteWorkplace ? 1 : 0.5, cursor: canDeleteWorkplace ? 'pointer' : 'not-allowed' }}
													>
														{deletingWorkplaceId === Number(workplace.id) ? (
															'...'
														) : (
															<Delete className={styles.deleteIcon} />
														)}
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>

							</div>
						) : !workplacesLoading && !workplacesError ? (
							<div className={styles.noDataMessage}>No workplaces found.</div>
						) : null}
					</div>
				</>
			);
		case 'create':
			return (
				<div className={styles.createWorkplaceContainer}>
					<CreateWorkplaceSetting backButtonClick={handleBackClick} nextUnitNumber={nextUnitNumber} />
				</div>
			);
		case 'edit':
      return (
				<div className={styles.showWorkplaceContainer}>
					{selectedWorkplace && <EditWorkplaceSettings workplace={selectedWorkplace} handleBackClick={handleBackClick} />}
				</div>
			);
    default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<h2 className={styles.formTitle}>Workplace Details</h2>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedWorkplace && <ShowWorkplaceSetting workplace={selectedWorkplace} />}
				</div>
			);
	}
}

export default WorkplaceSettings;
